/**
 * Per-clinician Exam2 preferred "normal" findings (localStorage, not PHI).
 * Keyed by Firebase uid so defaults follow the signed-in clinician on this device.
 */

const storageKey = (userId) =>
  `chartmind:exam2-defaults:${String(userId || "").trim() || "anonymous"}`;

/**
 * @returns {Record<string, string>} systemId → preferred normal finding
 */
export function loadExam2PreferredNormals(userId) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed)
        .map(([id, value]) => [String(id), String(value || "").trim()])
        .filter(([, value]) => Boolean(value)),
    );
  } catch {
    return {};
  }
}

/**
 * @param {string|null|undefined} userId
 * @param {Record<string, string>} preferredBySystemId
 */
export function saveExam2PreferredNormals(userId, preferredBySystemId) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      storageKey(userId),
      JSON.stringify(preferredBySystemId || {}),
    );
  } catch {
    // Quota / private mode — ignore; session UI still works for this visit.
  }
}

/**
 * @param {string|null|undefined} userId
 * @param {string} systemId
 * @param {string} finding
 * @returns {Record<string, string>}
 */
export function setExam2PreferredNormal(userId, systemId, finding) {
  const id = String(systemId || "").trim();
  const text = String(finding || "").trim();
  const next = { ...loadExam2PreferredNormals(userId) };
  if (!id || !text) return next;
  next[id] = text;
  saveExam2PreferredNormals(userId, next);
  return next;
}
